/**
 * Calendar API client.
 *
 * Hits workspace-scoped calendar endpoints. Base URL comes from
 * VITE_CALENDAR_API_BASE.
 */

import {
  CALENDAR_API_BASE,
  authHeaders,
  jsonHeadersWithToken,
  parseJsonSafe,
  getErrorMessage,
} from '@/lib/constants'
import { getBrowserTimezone } from './constants/calendarConstants'

const wsBase = (workspaceId) => {
  if (!workspaceId) throw new Error('workspaceId is required')
  return `${CALENDAR_API_BASE}/workspaces/${workspaceId}/calendar`
}

async function request(url, init = {}, fallbackError) {
  const headersWithAuth = init.body == null ? authHeaders() : jsonHeadersWithToken()
  const response = await fetch(url, {
    ...init,
    headers: {
      ...headersWithAuth,
      ...(init.headers || {}),
    },
  })
  if (!response.ok) {
    const errorData = await parseJsonSafe(response)
    throw new Error(
      getErrorMessage(errorData, `${fallbackError}: ${response.status} ${response.statusText}`)
    )
  }
  return parseJsonSafe(response)
}

// Backend stores Mongo `_id`; the frontend reads `.id` everywhere.
// Mirror `_id` onto `id` so callers don't have to know.
function normalizeOne(entity) {
  if (!entity || typeof entity !== 'object') return entity
  if (entity._id != null && entity.id == null) {
    return { ...entity, id: entity._id }
  }
  return entity
}

function normalizeList(list) {
  if (!Array.isArray(list)) return list
  return list.map(normalizeOne)
}

// ────────────────────────── Event types ──────────────────────────

export async function listEventTypes(workspaceId) {
  const result = await request(`${wsBase(workspaceId)}/event-types`, { method: 'GET' }, 'Failed to load event types')
  return normalizeList(result)
}

export async function getEventType(workspaceId, id) {
  const result = await request(`${wsBase(workspaceId)}/event-types/${id}`, { method: 'GET' }, 'Failed to load event type')
  return normalizeOne(result)
}

export async function createEventType(workspaceId, data) {
  const result = await request(
    `${wsBase(workspaceId)}/event-types`,
    { method: 'POST', body: JSON.stringify(data) },
    'Failed to create event type'
  )
  return normalizeOne(result)
}

export async function updateEventType(workspaceId, id, patch) {
  const result = await request(
    `${wsBase(workspaceId)}/event-types/${id}`,
    { method: 'PATCH', body: JSON.stringify(patch) },
    'Failed to update event type'
  )
  return normalizeOne(result)
}

export async function deleteEventType(workspaceId, id) {
  return request(
    `${wsBase(workspaceId)}/event-types/${id}`,
    { method: 'DELETE' },
    'Failed to delete event type'
  )
}

export async function duplicateEventType(workspaceId, id) {
  const result = await request(
    `${wsBase(workspaceId)}/event-types/${id}/duplicate`,
    { method: 'POST' },
    'Failed to duplicate event type'
  )
  return normalizeOne(result)
}

// ────────────────────────── Bookings ──────────────────────────

function appendParams(url, params = {}) {
  const target = new URL(url)
  Object.entries(params).forEach(([key, value]) => {
    if (value != null && value !== '') target.searchParams.set(key, value)
  })
  return target.toString()
}

export async function listBookings(workspaceId, filters = {}) {
  // Backend returns `{ data: Booking[], pagination }` — unwrap for consistency
  // with the rest of the calendar code, which deals in plain arrays.
  const result = await request(
    appendParams(`${wsBase(workspaceId)}/bookings`, {
      from: filters.from,
      to: filters.to,
    }),
    { method: 'GET' },
    'Failed to load bookings'
  )
  if (Array.isArray(result)) return normalizeList(result)
  if (Array.isArray(result?.data)) return normalizeList(result.data)
  return []
}

export async function getBooking(workspaceId, id) {
  const result = await request(
    `${wsBase(workspaceId)}/bookings/${id}`,
    { method: 'GET' },
    'Failed to load booking'
  )
  return normalizeOne(result)
}

export async function cancelBooking(workspaceId, id, reason = '') {
  const result = await request(
    `${wsBase(workspaceId)}/bookings/${id}/cancel`,
    { method: 'POST', body: JSON.stringify({ reason: reason || undefined }) },
    'Failed to cancel booking'
  )
  return normalizeOne(result)
}

export async function markNoShow(workspaceId, id) {
  const result = await request(
    `${wsBase(workspaceId)}/bookings/${id}/no-show`,
    { method: 'POST' },
    'Failed to mark no-show'
  )
  return normalizeOne(result)
}

export async function rescheduleBooking(workspaceId, id, { startsAt, endsAt }) {
  const result = await request(
    `${wsBase(workspaceId)}/bookings/${id}/reschedule`,
    { method: 'POST', body: JSON.stringify({ startsAt, endsAt }) },
    'Failed to reschedule booking'
  )
  return normalizeOne(result)
}

export async function exportBookingsCsv(workspaceId, filters = {}) {
  const url = appendParams(`${wsBase(workspaceId)}/bookings/export.csv`, {
    from: filters.from,
    to: filters.to,
  })
  const response = await fetch(url, {
    method: 'GET',
    headers: jsonHeadersWithToken(),
  })
  if (!response.ok) {
    const errorData = await parseJsonSafe(response)
    throw new Error(getErrorMessage(errorData, 'Failed to export bookings'))
  }
  const blob = await response.blob()
  const downloadUrl = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = downloadUrl
  a.download = `calendar-bookings-${Date.now()}.csv`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  window.URL.revokeObjectURL(downloadUrl)
  return { success: true }
}

export async function getCalendarAnalytics(workspaceId) {
  const timezone =
    typeof Intl !== 'undefined'
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : 'UTC'
  return request(
    `${wsBase(workspaceId)}/analytics?days=14&timezone=${encodeURIComponent(timezone || 'UTC')}`,
    { method: 'GET' },
    'Failed to load analytics'
  )
}

// ────────────────────────── Availability ──────────────────────────

export async function listAvailability(workspaceId) {
  const result = await request(
    appendParams(`${wsBase(workspaceId)}/availability`, {
      timezone: getBrowserTimezone(),
    }),
    { method: 'GET' },
    'Failed to load availability schedules'
  )
  return normalizeList(result)
}

export async function createAvailability(workspaceId, data) {
  const result = await request(
    `${wsBase(workspaceId)}/availability`,
    { method: 'POST', body: JSON.stringify(data) },
    'Failed to create schedule'
  )
  return normalizeOne(result)
}

export async function updateAvailability(workspaceId, id, patch) {
  const result = await request(
    `${wsBase(workspaceId)}/availability/${id}`,
    { method: 'PATCH', body: JSON.stringify(patch) },
    'Failed to update schedule'
  )
  return normalizeOne(result)
}

export async function deleteAvailability(workspaceId, id) {
  return request(
    `${wsBase(workspaceId)}/availability/${id}`,
    { method: 'DELETE' },
    'Failed to delete schedule'
  )
}

// ────────────────────────── Integrations ──────────────────────────

export async function listIntegrations(workspaceId) {
  return request(
    `${wsBase(workspaceId)}/integrations`,
    { method: 'GET' },
    'Failed to load integrations'
  )
}

/**
 * Toggle a calendar integration on or off.
 *
 * For `connected = true` the backend may either complete the connection inline
 * (returning the updated integration) or kick off an OAuth handshake (returning
 * `{ redirectUrl }`). When a `redirectUrl` is present we send the user there.
 */
export async function toggleIntegration(workspaceId, id, connected, payload = {}) {
  const path = connected ? 'connect' : 'disconnect'
  const body =
    connected && typeof window !== 'undefined'
      ? JSON.stringify({ returnTo: window.location.href, ...payload })
      : undefined
  const result = await request(
    `${wsBase(workspaceId)}/integrations/${id}/${path}`,
    { method: 'POST', ...(body ? { body } : {}) },
    `Failed to ${connected ? 'connect' : 'disconnect'} integration`
  )
  if (connected && result?.redirectUrl) {
    window.location.href = result.redirectUrl
  }
  return connected && result?.redirectUrl ? { ...result, pendingRedirect: true } : result
}
